<script setup lang="ts">
import { type AdminConsumerLandingPageSummary } from '@admin/serializers'
import { computed } from 'vue'

import { faPencil, faCopy, faTrash } from '@fortawesome/pro-light-svg-icons'
import DropDownActions from '@consumer/components/DropDownActions/DropDownActions.vue'
import type { DropDownActionItem } from '@consumer/components/DropDownActions/DropDownActions.vue'
import GIcon from '../GIcon.vue'

const props = defineProps<{
  moduleName: string
  module: { landingPages: AdminConsumerLandingPageSummary[] }
  editHref?: string
  onEdit?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  anchor?: 'left' | 'right'
}>()

const hasEdit = computed(() => !!props.editHref || !!props.onEdit)
const isSingleEdit = computed(() => hasEdit.value && !props.onDuplicate && !props.onDelete)

const dropdownItems = computed<DropDownActionItem[]>(() => {
  const items: DropDownActionItem[] = []

  if (hasEdit.value) {
    items.push({
      icon: faPencil,
      label: `Edit ${props.moduleName}`,
      href: props.editHref,
      // if you are going to a new page to edit something it will always be in a new tab
      newTab: !!props.editHref,
      onClick: props.editHref ? undefined : props.onEdit,
    })
  }

  if (props.onDuplicate) {
    const landingPageCount = props.module.landingPages.length
    items.push({
      icon: faCopy,
      label: `Duplicate ${props.moduleName}`,
      onClick: props.onDuplicate,
      disabled: landingPageCount <= 1,
    })
  }

  if (props.onDelete) {
    items.push({ icon: faTrash, label: `Delete ${props.moduleName}`, onClick: props.onDelete, danger: true })
  }

  return items
})
</script>

<template>
  <div class="landing-page-module-actions">
    <a
      v-if="isSingleEdit && editHref"
      :href="editHref"
      class="landing-page-module-actions__edit-btn"
      target="_blank"
      rel="noopener noreferrer"
    >
      Edit {{ moduleName }}
      <span class="sr-only">(opens in a new tab)</span>
    </a>
    <button
      v-else-if="isSingleEdit"
      type="button"
      class="landing-page-module-actions__edit-btn"
      @click="onEdit?.()"
    >
      Edit {{ moduleName }}
    </button>
    <div v-else class="landing-page-module-actions__dropdown">
      <DropDownActions :items="dropdownItems" :anchor="anchor ?? 'right'">
        <template #label>
          Modify {{ moduleName }}
        </template>
        <template #item="{ item }">
          <GIcon v-if="item.icon" :icon="item.icon" size="sm"/>
          <span v-if="item.icon === faCopy" class="flex flex-col">
            <span>{{ item.label }}</span>
            <span class="text-xs opacity-60">
              Appears on {{ module.landingPages.length }} {{ module.landingPages.length === 1
                ? 'page' : 'pages' }}
            </span>
          </span>
          <span v-else class="flex-1 min-w-0 truncate">{{ item.label }}</span>
        </template>
      </DropDownActions>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.landing-page-module-actions {
  --drop-down-actions-menu-max-width: 400px;
  --drop-down-actions-trigger-bg: white;
  --drop-down-actions-trigger-border: 1px solid #e4eaf1;
  --drop-down-actions-trigger-border-radius: 12px;
  --drop-down-actions-trigger-color: var(--color-text-active);
  --drop-down-actions-trigger-font-weight: 600;
  --drop-down-actions-trigger-padding: 0.5rem 1rem;
  --drop-down-actions-trigger-hover-bg: var(--color-bg-surface-blue-hover);

  white-space: nowrap;

  &__dropdown {
    display: contents;
  }

  &__edit-btn {
    background: var(--drop-down-actions-trigger-bg);
    border: var(--drop-down-actions-trigger-border);
    border-radius: var(--drop-down-actions-trigger-border-radius);
    color: var(--drop-down-actions-trigger-color);
    cursor: pointer;
    font-weight: var(--drop-down-actions-trigger-font-weight);
    padding: var(--drop-down-actions-trigger-padding);

    &:hover {
      background: var(--drop-down-actions-trigger-hover-bg);
    }

    &:focus-visible {
      outline: 2px solid var(--color-border-active);
      outline-offset: 2px;
    }
  }
}
</style>
